import { Injectable, inject, signal } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, map } from "rxjs";

export interface Event {
  id: number;
  year: number;
  name: string;
  slug: string;
  is_active: boolean;
  is_default: boolean;
  is_template: boolean;
  date_start: string;
  date_end: string | null;
  registration_deadline: string | null;
  is_registration_open: boolean;
  is_checkin_open: boolean;
  is_past: boolean;
  logo: string | null;
  location: string;
  config: any;
  config_schema: any;
  model_fields_schema: any;
  product_prefix: string | null;
  shop_enabled: boolean;
  pickup_point: string;
  template_pack: number | null;
  rules_link: string | null;
  requires_declaration: boolean;
  activities: Activity[];
  shirt_sizes: ShirtSize[];
  pricing_summary: {
    participants?: { price: number; remaining_seats: number | null };
    supporters?: { price: number; remaining_seats: number };
  } | null;
}

export interface ShirtSize {
  id: number;
  name: string;
  order: number;
  // Staff-toggleable flag separating Family-kid sizes from adult sizes
  // at registration. When true, the size is offered ONLY in the
  // paired-minor sub-form; when false, only on the main adult/regular
  // participant form.
  kid_size?: boolean;
  activities?: number[];
}

export interface Category {
  id: number;
  name: string;
  gender: string;
  age_start: number | null;
  age_end: number | null;
  // Family pairing v2 (#784): a Family registration is a single child
  // bracket (``requires_accompanying_adult``); the adult books the
  // activity's single ``is_family_companion`` row.
  requires_accompanying_adult?: boolean;
  is_family_companion?: boolean;
  // Per-category FRC opt-in. Honored only when the event's
  // ``support_frc_license`` is on; off = participants in this category
  // skip the FRC panel and the register-time gate entirely.
  requires_frc_license?: boolean;
  // Second-layer FRC gate: when set (e.g. "Elite"), the registrant's
  // extracted national_category must match (case-insensitive). Used
  // on Elite-tier categories to refuse Amator-grade cards. Blank /
  // omitted = only license validity is checked.
  required_frc_national_category?: string;
  // #742: per-category Product override (Family pricing). GET returns
  // the Product PK (or null); the staff editor may temporarily hold a
  // Product object before save and normalizes back to id at POST.
  product?: number | null;
}

export interface Activity {
  id: number;
  name: string;
  short_name: string;
  slug: string;
  description: string;
  color: string;
  category: string;
  listed: boolean;
  max_seats: number | null;
  max_seats_per_session: number | null;
  requires_adult_participant: boolean;
  requires_adult_participant_or_supporter: boolean;
  available_to_minors: boolean;
  categories: Category[];
  available_shirt_sizes: ShirtSize[];
  sessions: any[];
  config: {
    allows_supporters?: boolean;
    [key: string]: any;
  };
  config_schema: any;
  order: number;
  registration_open: boolean;
  checkin_open: boolean;
  payment_deadline: string | null;
  checkin_deadline: string | null;
  banner_wide: string | null;
  banner_mobile: string | null;
  include_in_order_description: boolean;
  itra_certified: boolean;
  min_bib: number;
  max_bib: number;
  show_counts: boolean;
  monochrome_logo: string | null;
  rules_link: string | null;
  addon_rules_link: string | null;
  override_use_addon_rules_link: boolean;
  declaration_text: string | null;
  third_party_declaration_text: string | null;
  shared_products?: any[];
  current_product?: any;
  // Issue #616: scheduling-conflict detection inputs.
  duration_minutes?: number | null;
  duration_is_minimum?: boolean;
  // Issue #681 / #682: per-activity custom fields (bicycle brand, etc.)
  custom_fields?: ActivityCustomField[];
}

/** One entry of `registration_data.competition_activities` (design §F.2(a)).
 *  `ActivitySerializer` plus the two keys the club card cannot derive:
 *  the server-normalised sizes and the configurable leader label (D24). */
export interface CompetitionActivity extends Activity {
  allowed_team_sizes: number[];
  leader_title: string;
  /** Disponibilul per mărime plafonată (spec „team_size_caps", 01.09).
   *  Cheia e mărimea ca ȘIR (JSON n-are chei numerice) și apar DOAR mărimile
   *  cu plafon: absența înseamnă „fără plafon". `{}` pe orice concurs fără
   *  `config.team_size_caps`, deci opțional pentru orice payload mai vechi. */
  team_size_availability?: Record<string, {
    cap: number; remaining: number; sold_out: boolean;
  }>;
}

export interface ActivityCustomField {
  id: number;
  name: string;
  slug: string;
  field_type: 'select' | 'text' | 'number';
  choices: string[];
  allows_other: boolean;
  required: boolean;
  order: number;
  help_text: string;
  show_in_stats: boolean;
}

export const CUSTOM_FIELD_OTHER_VALUE = '__other__';

export interface FrcPrefillResponse {
  token: string;
  extracted: any | null;
  prefill: {
    first_name?: string;
    last_name?: string;
    date_of_birth?: string;
    gender?: 'm' | 'f';
  };
  errors: string[];
}

export interface SupporterAgeCategory {
  id: number;
  name: string;
  age_min: number;
  age_max: number | null;
}

export interface SupporterProduct {
  id: number;
  name: string;
  description: string;
  unit_price: number;
  available_quantity: number | null;
  units_sold: number;
  config: {
    min_age?: number;
    max_age?: number;
  };
  reference: string;
  product_price_tier?: string;
  activity_id?: number | null;
  shared_pool_quantity?: number | null;
  pool_group_id?: string;
}

export interface RegistrationData {
  activities: Activity[];
  tour_activities: Activity[];
  transport_activities: Activity[];
  /** Competitions on sale. ABSENT when the event has none or the feature flag
   *  is off — read it as `?? []`, NEVER as a required key: that absence IS the
   *  inertia contract (design §G preamble). */
  competition_activities?: CompetitionActivity[];
  shirt_sizes: ShirtSize[];
  supporter_categories: SupporterAgeCategory[];
  supporter_products: SupporterProduct[];
  products: SupporterProduct[];
  event_rules: string;
  liability_release: string;
  requires_declaration: boolean;
  config: any;
  event_date: string;
  checkin_deadline: string | null;
  event_id: number;
  product_prefix: string | null;
  logo: string | null;
  pricing_period: {
    price: number;
    available_until: string;
    remaining_seats: number;
    tier?: string;
  } | null;
  pricing_period_per_activity: Record<number, {
    price: number;
    available_until: string;
    remaining_seats: number;
    tier?: string;
    pool_key?: string;
  }>;
  pricing_per_category?: { [key: string]: { price: number; product_id: number } };
  all_activities_have_limits: boolean;
  supporter_pricing_period: {
    price: number;
    available_until: string;
    remaining_seats: number;
    tier?: string;
  } | null;
  event_name: string;
  user_participant?: Participant | null;
  registered_participants?: Participant[];
  payment_initiated_order_exists?: boolean;
  payment_initiated_order_id?: number | null;
  payment_rejected_order_exists?: boolean;
  payment_rejected_order_id?: number | null;
  /** Experience (tour/transport) lines already in the user's DRAFT order,
   *  so the edit flow can preload the picks. Aggregated per session. */
  draft_experiences?: Array<{
    session_id: number;
    activity_id: number;
    seats: number;
    category: "tour" | "transport";
  }> | null;
  free_experiences?: {
    rules: Array<{ activities: number[]; tours: number[] }>;
    used: number;
  } | null;
  /** Event gated behind a personal access code (must clients). */
  requires_access_code?: boolean;
  /** The current user already has a code bound to this event. */
  has_access?: boolean;
  /** Show per-participant prices in the registration wizard. Absent = shown. */
  show_prices_on_registration?: boolean;
  /** Show supporter prices in the registration wizard. Absent = shown. */
  show_supporter_prices_on_registration?: boolean;
  /** Custom heading for the access-code gate. Empty = default RO text. */
  access_code_gate_title?: string;
  /** Custom info text (Markdown) for the access-code gate. Empty = default. */
  access_code_gate_text?: string;
}

export interface Participant {
  id: number;
  first_name: string;
  last_name: string;
  email: string | null;
  phone_number: string;
  date_of_birth: string | null;
  gender: string;
  town: string;
  county: string;
  country: string;
  activity: any;
  session: any;
  category: any;
  shirt_size: any;
  primary_activity_id?: number | null;
  primary_session_id?: number | null;
  primary_category_id?: number | null;
  is_club_member: boolean;
  club_name: string;
  itra_id: string;
  is_self: boolean;
  shirt_size_obj: number | null;
  type: string;
  status: string;
  parent_participant: number | null;
  supporters: Participant[];
  minors: Participant[];
  bookings?: any[];
  activity_name: string | null;
  activity_color: string;
  category_name: string | null;
  /** Table-seating label "Zona · masa X · loc Y" (null when no reservation). */
  seat?: string | null;
  rules_agreement: boolean;
  waiver_agreement: boolean;
  dashboard_edit_url: string | null;
  register_dependent_url: string | null;
  can_register_dependents: boolean;
  manual_waiver_link: string | null;
  is_adult: boolean;
  is_minor: boolean;
  /** Adult/minor AT THE EVENT DATE ("adult" | "minor" | null). A NEW field
   *  independent of is_adult/is_minor (which stay as-is for the club
   *  dashboard + /me/); drives the staff detail role-badge age suffix. */
  age_group?: "adult" | "minor" | null;
  // Family enrolment context for a BOOKINGLESS Family KID (draft pair) on the
  // staff list — event/activity resolved server-side from the exact paired
  // OrderProduct (#541). null for confirmed kids (they carry own `bookings`)
  // and for every non-KID row.
  family_context?: { event_name: string; activity_name: string } | null;
  paired_minor?: {
    first_name: string;
    last_name: string;
    date_of_birth: string;
    gender: string;
    shirt_size_obj?: number | null;
  } | null;
  product?: any;
  can_delete: boolean;
  order_status?: number | null;
  order_id?: number | null;
  order_updated_at?: string | null;
  // Fields exposed by ParticipantSerializer (fields="__all__") used on
  // the dashboard event-detail participant card. All optional because
  // some only populate after specific events (bib at check-in, FRC for
  // cycling-only events, etc.).
  bib_number?: string | null;
  shirt_size_name?: string | null;
  frc_license_status?: string | null;
  // Set when this person was marked as attending in check-in Step 1. #418: the
  // combined-ticket cohort is `active && attending_confirmed_at`, so the SPA
  // needs it to decide the ticket buttons the same way the backend decides the
  // response. Emitted by ParticipantSerializer (fields="__all__") and, since
  // #418 r3, by NestedParticipantSerializer for minors/supporters too.
  attending_confirmed_at?: string | null;
  // True when this participant is the kid half of an active Family-pair
  // OrderProduct (paired_participant_id on the adult's OP references this
  // participant). The dashboard hides the standalone delete trash on
  // Family kids — they can only be removed by editing the adult and
  // switching off the Family category.
  is_family_kid?: boolean;
}

// Issue #617 (Phase 2 / T2.3): per-booking summary surfaced on the dashboard.
export interface PurchasedExperience {
  booking_id: number;
  participant_id: number;
  participant_name: string;
  activity_id: number;
  activity_name: string;
  activity_category: 'tour' | 'transport' | string;
  session_id: number;
  session_start_time: string | null;
  seats: number;
  order_id: number;
  order_status: number | null;
  qr_hash: string | null;
}

export interface InFlightExperienceOrder {
  order_id: number;
  order_status: number;
  order_product_id: number;
  product_name: string;
  product_reference: string;
  category: 'tour' | 'transport' | string | null;
  activity_id: number | null;
  activity_name: string;
  session_id: number | null;
  session_start_time: string | null;
  quantity: number;
  unit_price: number;
  total_price: number;
  /** True for a free experience (priced 0); the cart shows "Gratuit". */
  free_experience?: boolean;
  resume_payment_url: string;
  /** True only while order is DRAFT — false once PAYMENT_INITIATED. */
  editable: boolean;
}

export interface OversoldBooking {
  order_id: number;
  order_product_id: number;
  product_name: string;
  activity_name: string | null;
  session_start_time: string | null;
  seats_requested: number | null;
  session_max: number | null;
}

/**
 * Slim event-header payload (#715). The dashboard / experiences-buy
 * frontend only reads these fields — the heavy `activities`,
 * `shirt_sizes`, and `pricing_summary` from the regular `Event`
 * interface are intentionally NOT here. If you need those, use
 * `getRegistrationData` (form data) or `getEvent` (full detail).
 *
 * Returned by `/events/<id>/header/`, cacheable for ~60s in the
 * browser since it's not user-scoped.
 */
export interface EventHeader {
  id: number;
  name: string;
  slug: string;
  year: number;
  is_active: boolean;
  is_default: boolean;
  is_template: boolean;
  date_start: string;
  date_end: string | null;
  registration_deadline: string | null;
  is_registration_open: boolean;
  is_checkin_open: boolean;
  is_past: boolean;
  logo: string | null;
  location: string;
  config: any;
  product_prefix: string | null;
  template_pack: number | null;
  rules_link: string | null;
  requires_declaration: boolean;
  shop_enabled: boolean;
}

/**
 * User-specific dashboard state (#715). Returned by
 * `/events/<id>/my_dashboard_state/`. Pair with `getEventHeader` and
 * `getRegistrationData` for the full dashboard view.
 *
 * Splitting the previous `MyEventDetails` megaservice lets each piece
 * cache appropriately — event header + registration data are not
 * user-scoped, this one is.
 */
export interface DashboardVoucher {
  code: string;
  value: string | null;
  quantifier: string | null;
  is_percentage: boolean;
  product_domains: string;
  /** Human label of where it applies, already composed by the backend —
   *  includes the activity restriction when the voucher has one, e.g.
   *  `înscriere — doar „Cursa 10 km"`. */
  usable_label: string;
  expiration_date: string | null;
  is_valid: boolean;
  /** "valid" | "expired" | "used" */
  status: string;
  /** `max_persons - used_persons` for a person-pool voucher; null when the
   *  voucher has no pool. 0 means the pool is spent — the voucher then also
   *  reports is_valid=false / status="used".
   *
   *  Not optional: the backend emits the key on every voucher row
   *  (events/views.py, `my_dashboard_state`), and an optional field would let a
   *  consumer treat "no pool" and "field missing" as two states when only one
   *  exists. */
  persons_remaining: number | null;
}

/**
 * One competition team of the signed-in account, on the event dashboard (PR6,
 * design §L.6 crit. 4, I-34).
 *
 * The shape is the CHECK-IN WIZARD's own team payload
 * (`events/checkin_wizard.py::_competition_team_payload`, §F.2(b)) MINUS
 * `can_manage`, `is_account_team` and `account_owner_name`: the dashboard is
 * read-only, names no other account and offers no button. There is NO money in
 * it — the unit sold is the team (B1) and the amounts live on the order rows.
 */
export interface DashboardCompetitionTeam {
  id: number;
  activity_id: number;
  activity_name: string;
  session: {
    id: number; start_time: string | null; end_time: string | null;
    max_seats: number | null; seats_left: number | null;
  };
  name: string;
  selected_size: number;
  /** "pending_payment" | "active" — a cancelled team is never emitted. */
  status: string;
  /** ROWS, not eligible people: a seat is held by a row (§D.5). */
  member_count: number;
  member_count_eligible: number;
  free_slots: number;
  is_complete: boolean;
  has_captain: boolean;
  /** `Event.config["competition_leader_title"]` (D24) — never hardcoded in the
   *  frontend. */
  leader_title: string;
  bib: number | null;
  members: Array<{
    participant_id: number; name: string; role: string;
    /** True for the account's own people — the card labels the rest as
     *  teammates rather than as its own participants. */
    is_own: boolean;
    is_eligible: boolean;
  }>;
}

export interface DashboardState {
  participants: Participant[];
  orders: any[];
  oversold_bookings: OversoldBooking[];
  purchased_experiences: PurchasedExperience[];
  in_flight_orders: InFlightExperienceOrder[];
  // Vouchers tied to the current user + this event (e.g. a refund voucher
  // from an owner downgrade). Optional for backward-compat with cached payloads.
  vouchers?: DashboardVoucher[];
  free_experiences?: {
    eligible: boolean;
    quota: number;
    used: number;
    remaining: number;
    tour_activity_ids: number[];
  } | null;
  /** ABSENT — not empty — when the event has no competitions, when the flag is
   *  off, or when the account has no live team (§J.3's inertia contract). Read
   *  it as `state.competition_teams ?? []`. */
  competition_teams?: DashboardCompetitionTeam[];
  /** v5.4 crit. 17/19 — the SAME dict `checkin/state/` publishes, from the same
   *  service (`account_team_caps`). STRING keys, because JSON. Absent with the
   *  flag off; present at ZERO teams when the event HAS competitions, which is
   *  precisely the state the „Cumpără experiențe" link lives in. */
  competition_team_caps?: Record<string, { used: number; max: number }>;
}

/**
 * Legacy composite shape returned by `/events/<id>/my_event_details/`.
 * @deprecated Migrate to the three split endpoints (#715):
 *   - `getEventHeader(id)` for `event`
 *   - `getRegistrationData(id)` for `registration_data`
 *   - `getMyDashboardState(id)` for the rest
 * The legacy endpoint stays available during migration but its `event`
 * field returns empty arrays for `activities`/`shirt_sizes` and null
 * for `pricing_summary` — see #714. New code shouldn't rely on it.
 */
export interface MyEventDetails extends DashboardState {
  event: Event;
  registration_data: RegistrationData;
}

/** One team the „Cumpără experiențe" page asks for. §F.2(a)'s dialect — the
 *  SAME one `register` speaks: `session`, never `session_id`, and NO captain
 *  key (the leader is derived server-side, or the team is born without one). */
export interface BuyCompetitionTeam {
  activity_id: number;
  session: number;
  team_size: number;
  team_name: string;
  /** „Accept regulamentul concursului", per ECHIPĂ. Trimis DOAR
   *  când activitatea publică un `rules_link`: pe restul concursurilor cheia
   *  lipsește cu totul, iar payload-ul rămâne cel de dinaintea regulii. Fără el,
   *  serverul refuză cu 400 `competition_rules_not_accepted`. */
  rules_accepted?: boolean;
  /** VERSIUNEA acceptată: URL-ul regulamentului AFIȘAT în clipa bifării, nu cel
   *  curent de pe activitate. Călătorește împreună cu `rules_accepted: true`;
   *  serverul îl compară și refuză cu 409 `competition_rules_changed` dacă
   *  organizatorul a republicat documentul cât timp formularul era deschis. */
  rules_link?: string;
}

// Issue #617: POST /events/<id>/experiences/buy/ contract.
export interface BuyExperiencesPayload {
  tours: Array<{ session_id: number; seats: number; participant_id?: number | null }>;
  transport: Array<{ session_id: number; seats: number; participant_id?: number | null }>;
  /** v5.4 crit. 17. OPTIONAL, and omitted entirely when empty: that absence is
   *  what keeps this endpoint byte-identical for every tour buyer (crit. 11's
   *  „byte-identical without the key"). */
  competition_teams?: BuyCompetitionTeam[];
}
export interface BuyExperiencesResponse {
  order_id: number;
  total_amount: string;
  status: number;
  payment_url: string | null;
  warnings?: any[];
  /** Present only when THIS request created teams (the echo `register` returns
   *  too). At total 0 the rows are already ACTIVE here — the door confirms the
   *  basket inline and re-reads them before serialising. */
  competition_teams?: Array<{
    id: number; activity_id: number; session_id: number; name: string;
    selected_size: number; status: string; free_slots: number;
    /** `null` on a captain-less team — a NORMAL state since v5.4. */
    leader: { participant_id: number; name: string } | null;
  }>;
}

@Injectable({
  providedIn: "root",
})
export class EventsService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = "/api/v1/events/events/";
  private readonly participantsApiUrl = "/api/v1/events/participants/";

  readonly currentActivityColor = signal<string | null>(null);

  getUpcomingEvents(): Observable<Event[]> {
    return this.http.get<Event[]>(`${this.apiUrl}?upcoming=true`);
  }

  getAllEventsForHome(): Observable<Event[]> {
    return this.http.get<Event[]>(`${this.apiUrl}?all_for_home=true`);
  }

  getRegistrationData(eventId: number): Observable<RegistrationData> {
    return this.http.get<RegistrationData>(`${this.apiUrl}${eventId}/registration_data/`);
  }

  register(eventId: number, data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}${eventId}/register/`, data);
  }

  /** Participant access QR as a PNG blob (auth token carried by the
   *  HTTP interceptor — a bare <a download> wouldn't send it). */
  downloadParticipantQr(participantId: number): Observable<Blob> {
    return this.http.get(`${this.participantsApiUrl}${participantId}/qr/`, {
      responseType: "blob",
    });
  }

  /** Participant PDF ticket (one page, plus a master page for the account
   *  owner). Auth token carried by the HTTP interceptor. */
  downloadParticipantTicket(participantId: number): Observable<Blob> {
    return this.http.get(`${this.participantsApiUrl}${participantId}/ticket/`, {
      responseType: "blob",
    });
  }

  /** The full combined ticket set for the signed-in owner's registration on
   *  an event — the same multi-page PDF the check-in finalize step emails
   *  (owner master page first, then one page per attending participant). */
  downloadAllTickets(eventId: number): Observable<Blob> {
    return this.http.get(
      `/api/v1/events/checkin/tickets/?event_id=${eventId}`,
      { responseType: "blob" },
    );
  }

  /**
   * #680 cycling FRC prefill flow — upload a card BEFORE the participant
   * exists. Backend stores it on a ``PendingFRCExtraction`` row keyed by
   * ``token``, runs Claude Vision (#679), and returns prefill values
   * for the registration form.
   */
  uploadFrcCardForPrefill(eventId: number, file: File): Observable<FrcPrefillResponse> {
    const fd = new FormData();
    fd.append('frc_license_card', file);
    return this.http.post<FrcPrefillResponse>(
      `${this.apiUrl}${eventId}/frc_card_extract/`, fd,
    );
  }

  /** Pre-registration club-documents upload (multi-file). Returns a token to
   *  pass back on the register payload as ``club_docs_token``. */
  uploadClubDocs(eventId: number, files: File[] | FileList): Observable<{ token: string; files: { name: string }[]; errors: string[] }> {
    const fd = new FormData();
    Array.from(files).forEach(f => fd.append('files', f));
    return this.http.post<{ token: string; files: { name: string }[]; errors: string[] }>(
      `${this.apiUrl}${eventId}/club_docs_upload/`, fd,
    );
  }

  /** Slim event-header payload for the dashboard (#715). Skips the
   * activities/shirt_sizes/pricing_summary tree that the regular
   * `getEvent` would emit. Cacheable for ~60s. */
  getEventHeader(eventId: number): Observable<EventHeader> {
    return this.http.get<EventHeader>(`${this.apiUrl}${eventId}/header/`);
  }

  /** Full event detail — for staff editing or similar. Heavy. The
   * dashboard should use `getEventHeader` instead. */
  getEvent(eventId: number): Observable<Event> {
    return this.http.get<Event>(`${this.apiUrl}${eventId}/`);
  }

  /** User-specific dashboard state (#715). Pair with `getEventHeader`
   * and `getRegistrationData` for the full dashboard view. */
  getMyDashboardState(eventId: number): Observable<DashboardState> {
    return this.http.get<DashboardState>(`${this.apiUrl}${eventId}/my_dashboard_state/`);
  }

  /** @deprecated Use `getEventHeader` + `getRegistrationData` +
   * `getMyDashboardState` in parallel. See #715. */
  getMyEventDetails(eventId: number): Observable<MyEventDetails> {
    return this.http.get<MyEventDetails>(`${this.apiUrl}${eventId}/my_event_details/`);
  }

  /** Issue #617 (Phase 2): post-payment buy of addons + transport. */
  buyExperiences(eventId: number, payload: BuyExperiencesPayload): Observable<BuyExperiencesResponse> {
    return this.http.post<BuyExperiencesResponse>(`${this.apiUrl}${eventId}/buy_experiences/`, payload);
  }

  getParticipant(participantId: number): Observable<Participant> {
    return this.http.get<Participant>(`${this.participantsApiUrl}${participantId}/`);
  }

  updateParticipant(participantId: number, data: any): Observable<Participant> {
    return this.http.put<Participant>(`${this.participantsApiUrl}${participantId}/`, data);
  }

  /** Partial update — used by the edit flow to send only `{tours, transport}`
   *  (experience replace) without a full participant representation, which a
   *  PUT would reject. The backend rebuilds the participant's experience
   *  OrderProducts from the submitted set. */
  patchParticipant(participantId: number, data: any): Observable<Participant> {
    return this.http.patch<Participant>(`${this.participantsApiUrl}${participantId}/`, data);
  }

  deleteParticipant(participantId: number): Observable<any> {
    return this.http.delete(`${this.participantsApiUrl}${participantId}/`);
  }

  addSupporter(participantId: number, supporterData: any): Observable<Participant> {
    return this.http.post<Participant>(`${this.participantsApiUrl}${participantId}/add_supporter/`, supporterData);
  }

  // Owner self-service activity change (Phase 1). Dry-run: returns
  // {delta, outcome: 'free'|'refund'|'upcharge', refund_amount,
  //  upcharge_amount, phase1_self_serviceable, ...}.
  selfTransferPreview(participantId: number, body: { target_activity: number; target_session?: number | null; target_category?: number | null; move_dependents?: number[]; paired_minor?: any }): Observable<any> {
    return this.http.post<any>(`${this.participantsApiUrl}${participantId}/self-transfer-preview/`, body);
  }

  // Execute the change (delta <= 0 only in Phase 1). On a downgrade the
  // response carries {refund_voucher: {code, value}}. A Family target also
  // accepts paired_minor (the child to create on the new activity).
  selfTransfer(participantId: number, body: { target_activity: number; target_session?: number | null; target_category?: number | null; move_dependents?: number[]; paired_minor?: any }): Observable<any> {
    return this.http.post<any>(`${this.participantsApiUrl}${participantId}/self-transfer/`, body);
  }

  // Phase 2: initiate payment for an upcharge transfer. Returns {url, data}
  // (Netopia redirect) or {zero_sum: true, redirect_url} for an edge-case
  // zero-sum result.
  initiateSelfTransferUpgrade(participantId: number, body: { target_activity: number; target_session?: number | null; target_category?: number | null; move_dependents?: number[]; paired_minor?: any }): Observable<any> {
    return this.http.post<any>(`${this.participantsApiUrl}${participantId}/self-transfer/initiate-upgrade/`, body);
  }

  removeSupporter(participantId: number, supporterId: number): Observable<any> {
    return this.http.delete(`${this.participantsApiUrl}${participantId}/remove_supporter/${supporterId}/`);
  }

  getWaiverData(token: string): Observable<any> {
    return this.http.get(`${this.participantsApiUrl}verify_waiver/?token=${encodeURIComponent(token)}`);
  }

  verifyWaiver(token: string, rulesAgreement: boolean, waiverAgreement: boolean): Observable<any> {
    return this.http.post(`${this.participantsApiUrl}verify_waiver/`, {
      token,
      rules_agreement: rulesAgreement,
      waiver_agreement: waiverAgreement,
    });
  }

  getPublicConfig(): Observable<any> {
    const constanceUrl = "/api/v1/events/constance/public/";
    return this.http.get<any>(constanceUrl);
  }
}
